import { useState } from "react";
import { Group, Panel } from "react-resizable-panels";

import { AssessmentToolType, LearnerFileType } from "@api/graphql/types-and-hooks";
import { Loader } from "@components/ClinicalNewReportDialog/Loader";
import { DocumentReviewHeader } from "@components/DocumentReviewModal/components";
import { DocumentEditor } from "@components/DocumentReviewModal/components/DocumentEditor";
import { DocumentViewer } from "@components/DocumentReviewModal/components/DocumentViewer";

export interface DocumentReviewModalProps {
  fileId?: string;
  existingFiles: LearnerFileType[];
  supportedAssessments: AssessmentToolType[];
  setFileId: (fileId: string) => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

export const DocumentReviewModalInner: React.FC<DocumentReviewModalProps> = ({
  fileId,
  existingFiles,
  setFileId,
  supportedAssessments,
  onDirtyChange,
}) => {
  const currentFileIndex = existingFiles.findIndex((file) => file.id === fileId);
  const currentFile = existingFiles[currentFileIndex];

  const [leftPanelSize, setLeftPanelSize] = useState(800);

  if (!fileId) return null;

  if (currentFileIndex === -1 || !currentFile) {
    // existingFiles loaded but this ID isn't in the list — file was likely deleted
    if (existingFiles.length > 0) {
      return (
        <div className="flex items-center justify-center h-full">
          <p className="text-sm text-text-secondary">File not found.</p>
        </div>
      );
    }
    // File list may be mid-refetch — show a spinner while we wait
    return (
      <div className="flex items-center justify-center h-full">
        <Loader className="w-6 h-6 mr-0 ml-0 text-text-secondary" />
      </div>
    );
  }

  return (
    <>
      <DocumentReviewHeader
        fileName={currentFile.originalFileName}
        fileSize={currentFile.fileSize}
        skillType={currentFile.fileKind}
        currentFileIndex={currentFileIndex + 1}
        totalFiles={existingFiles.length}
        supportedAssessments={supportedAssessments}
        onPrevious={
          existingFiles.length > 1
            ? () => {
                const prevIndex = (currentFileIndex - 1 + existingFiles.length) % existingFiles.length;
                setFileId(existingFiles[prevIndex].id);
              }
            : undefined
        }
        onNext={
          existingFiles.length > 1
            ? () => {
                const nextIndex = (currentFileIndex + 1) % existingFiles.length;
                setFileId(existingFiles[nextIndex].id);
              }
            : undefined
        }
      />
      <Group>
        <Panel
          minSize="50%"
          data-testId="preview"
          className="bg-limestone-100 h-full"
          onResize={(panelSize) => {
            setLeftPanelSize(panelSize.inPixels);
          }}
        >
          {currentFile && <DocumentViewer file={currentFile} width={Math.min(leftPanelSize - 40, 1200)} />}
        </Panel>
        <DocumentEditor file={currentFile} onDirtyChange={onDirtyChange} />
      </Group>
    </>
  );
};
